import React, { useState, useEffect, useRef } from "react";
import { CustomIcon } from "../shared/CustomIcon";
import { SORT_OPTIONS } from "./MembersFilterPanel";

// ============================================================
// Iter 86c — FlockToolbarMenu
// Kebab (⋮) overflow menu that replaces the individual FILTER /
// SORT / SELECT pill buttons. Renders three primary items —
// Filter, Sort ›, Select (SA-only) — and a nested Sort submenu.
// While in selection mode the primary Select entry flips to
// "Select All" / "Deselect All" (tracks `allSelected`) and adds
// an "Exit selection" entry so the mode can be closed from here.
// ============================================================
export function FlockToolbarMenu({ isSuperAdmin, selectMode, allSelected, sortBy, onFilter, onSort, onEnterSelect, onExitSelect, onSelectAllToggle }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("root"); // "root" | "sort"
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setView("root"); } };
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); setView("root"); } };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const close = () => { setOpen(false); setView("root"); };
  const itemStyle = {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    width: "100%", padding: "11px 14px", background: "transparent", border: "none",
    color: "var(--kc-text)", fontFamily: "'Montserrat',sans-serif", fontSize: 13,
    fontWeight: 600, letterSpacing: "0.02em", textAlign: "left", cursor: "pointer",
  };
  return (
    <div ref={ref} style={{ position: "relative", flex: "0 0 auto" }}>
      <button
        type="button"
        data-testid="members-toolbar-menu-btn"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => { setOpen(o => !o); setView("root"); }}
        style={{
          width: 36, height: 36, borderRadius: 999,
          background: "color-mix(in srgb, var(--kc-text) 8%, transparent)",
          border: "1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)",
          color: "var(--kc-text)", cursor: "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, lineHeight: 1, padding: 0,
        }}
      >
        ⋮
      </button>
      {open && (
        <div
          data-testid="members-toolbar-menu"
          role="menu"
          style={{
            position: "absolute", right: 0, top: "calc(100% + 6px)",
            background: "var(--kc-elevated)",
            border: "1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent)",
            borderRadius: 10, minWidth: 220, padding: "6px 0",
            zIndex: 400, boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
          }}
        >
          {view === "root" && (
            <>
              <button
                type="button"
                role="menuitem"
                data-testid="members-menu-filter"
                onClick={() => { onFilter(); close(); }}
                style={itemStyle}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                  <CustomIcon name="tools" size={14} /> Filter
                </span>
              </button>
              <button
                type="button"
                role="menuitem"
                data-testid="members-menu-sort"
                onClick={() => setView("sort")}
                style={itemStyle}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                  <span aria-hidden="true">⇅</span> Sort
                </span>
                <span aria-hidden="true" style={{ color: "var(--kc-sub)" }}>›</span>
              </button>
              {isSuperAdmin && !selectMode && (
                <button
                  type="button"
                  role="menuitem"
                  data-testid="members-menu-select"
                  onClick={() => { onEnterSelect(); close(); }}
                  style={itemStyle}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                    <CustomIcon name="check" size={14} /> Select
                  </span>
                </button>
              )}
              {isSuperAdmin && selectMode && (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    data-testid="members-select-all-btn"
                    onClick={() => { onSelectAllToggle(); close(); }}
                    style={itemStyle}
                    aria-pressed={allSelected}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                      <CustomIcon name="check" size={14} />
                      {allSelected ? "Deselect All" : "Select All"}
                    </span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    data-testid="members-menu-exit-select"
                    onClick={() => { onExitSelect(); close(); }}
                    style={itemStyle}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 10, color: "var(--kc-sub)" }}>
                      × Exit selection
                    </span>
                  </button>
                </>
              )}
            </>
          )}
          {view === "sort" && (
            <>
              <button
                type="button"
                role="menuitem"
                data-testid="members-menu-sort-back"
                onClick={() => setView("root")}
                style={{ ...itemStyle, color: "var(--kc-sub)", fontSize: 12 }}
              >
                <span>‹ Back</span>
              </button>
              {SORT_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  role="menuitem"
                  data-testid={`members-menu-sort-${opt.id}`}
                  onClick={() => { onSort(opt.id); close(); }}
                  style={{
                    ...itemStyle,
                    color: opt.id === sortBy ? "var(--kc-accent)" : "var(--kc-text)",
                  }}
                >
                  <span>{opt.label}</span>
                  {opt.id === sortBy && <span style={{ color: "var(--kc-accent)", display: "inline-flex" }}><CustomIcon name="check" size={14} /></span>}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
